import { useMemo, useState } from 'react';
import { useActiveFleet } from '@/data/fleets';
import { usePostings, useCreatePosting, useCancelPosting, useCompletePosting } from '@/data/load-postings';
import { useLoads } from '@/data/loads';
import {
  postingStatusTone,
  postingStatusLabel,
  postingModeLabel,
} from '@/lib/load-posting-status';
import { formatDateTime, formatMoney } from '@/lib/format';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { TableScroll } from '@/components/ui/table-scroll';
import { cn } from '@/lib/cn';
import type { LoadPostingStatus } from '@breaker/shared-types';
import type { CreatePostingBody } from '@breaker/shared-schemas';

const STATUS_FILTERS: Array<{ value: LoadPostingStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function LoadBoardPage() {
  const { fleet } = useActiveFleet();
  const fleetId = fleet?.id ?? null;
  const [posting, setPosting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<LoadPostingStatus | 'all'>('all');

  const postingsQuery = usePostings(
    fleetId,
    statusFilter === 'all' ? {} : { status: statusFilter },
  );
  const cancelPosting = useCancelPosting(fleetId);
  const completePosting = useCompletePosting(fleetId);

  return (
    <div className="px-8 py-10 max-w-[1100px]">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
            Load board
          </h1>
          <p className="text-sm text-breaker-mute mt-1">
            Post loads to your drivers. Claimable loads are taken by the
            first driver to claim; direct-assign loads you allocate yourself.
          </p>
        </div>
        <Button size="sm" onClick={() => setPosting(true)}>
          Post a load
        </Button>
      </header>

      <div className="mb-4 flex items-center gap-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setStatusFilter(f.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm transition-colors',
              statusFilter === f.value
                ? 'bg-breaker-ink text-white'
                : 'text-breaker-ink-2 hover:bg-breaker-ground-2',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {postingsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : postingsQuery.isError ? (
        <EmptyState
          title="Could not load postings"
          description={(postingsQuery.error as Error)?.message ?? 'Server error.'}
        />
      ) : (postingsQuery.data ?? []).length === 0 ? (
        <EmptyState
          title="No postings yet"
          description="Post a load to your drivers with the Post a load button."
        />
      ) : (
        <div className="surface rounded-lg overflow-hidden">
          <TableScroll>
          <table className="w-full text-sm">
            <thead className="bg-breaker-ground-2 border-b border-breaker-line">
              <tr className="text-left">
                <Th>Target</Th>
                <Th className="w-[110px]">Status</Th>
                <Th className="w-[130px]">Mode</Th>
                <Th className="w-[100px]">Rate</Th>
                <Th>Claimed by</Th>
                <Th className="w-[160px]">Posted</Th>
                <Th className="w-[160px]" />
              </tr>
            </thead>
            <tbody>
              {(postingsQuery.data ?? []).map((j) => (
                <tr
                  key={j.id}
                  className="border-b last:border-b-0 border-breaker-line hover:bg-breaker-ground-2 transition-colors"
                >
                  <Td>
                    <div className="font-medium text-breaker-ink">
                      {j.targetReference ?? '\u2014'}
                    </div>
                    <div className="text-2xs uppercase tracking-wider text-breaker-mute">
                      {j.stopCount !== null
                        ? `${j.stopCount} ${j.stopCount === 1 ? 'stop' : 'stops'}`
                        : 'load'}
                    </div>
                  </Td>
                  <Td>
                    <Badge tone={postingStatusTone(j.status)}>
                      {postingStatusLabel(j.status)}
                    </Badge>
                  </Td>
                  <Td className="text-breaker-ink-2">{postingModeLabel(j.assignmentMode)}</Td>
                  <Td className="text-breaker-ink-2">
                    {j.rateAmount !== null ? (
                      <span>
                        {formatMoney(j.rateAmount)}
                        {!j.rateVisible ? (
                          <span className="ml-1 text-2xs text-breaker-mute">(hidden)</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-breaker-mute">{'\u2014'}</span>
                    )}
                  </Td>
                  <Td className="text-breaker-ink-2">
                    {j.claimedByName ?? <span className="text-breaker-mute">{'\u2014'}</span>}
                  </Td>
                  <Td className="text-breaker-ink-2">{formatDateTime(j.postedAt)}</Td>
                  <Td>
                    <div className="flex justify-end gap-2">
                      {j.status === 'claimed' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          action={() =>
                            completePosting
                              .mutateAsync({ postingId: j.id })
                              .catch(() => undefined)
                          }
                          loading={completePosting.isPending && completePosting.variables?.postingId === j.id}
                        >
                          Complete
                        </Button>
                      ) : null}
                      {j.status === 'open' || j.status === 'claimed' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          action={() =>
                            cancelPosting
                              .mutateAsync({ postingId: j.id, body: {} })
                              .catch(() => undefined)
                          }
                          loading={cancelPosting.isPending && cancelPosting.variables?.postingId === j.id}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </div>
      )}

      <PostJobDialog
        open={posting}
        fleetId={fleetId}
        onClose={() => setPosting(false)}
      />
    </div>
  );
}

function PostJobDialog({
  open,
  fleetId,
  onClose,
}: {
  open: boolean;
  fleetId: string | null;
  onClose: () => void;
}) {
  const post = useCreatePosting(fleetId);
  const loadsQuery = useLoads(fleetId);

  const [targetId, setTargetId] = useState('');
  const [mode, setMode] = useState<'claimable' | 'assign'>('claimable');
  const [rate, setRate] = useState('');
  const [rateVisible, setRateVisible] = useState(true);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const targetOptions = useMemo(
    () =>
      (loadsQuery.data ?? []).map((d) => ({
        id: d.id,
        label: `${d.reference} (${d.status})`,
      })),
    [loadsQuery.data],
  );

  function reset() {
    setTargetId('');
    setMode('claimable');
    setRate('');
    setRateVisible(true);
    setNotes('');
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  function onSubmit() {
    setError(null);
    if (!targetId) {
      setError('Choose a load to post.');
      return Promise.resolve();
    }
    const body: CreatePostingBody = {
      loadId: targetId,
      audience: 'internal',
      assignmentMode: mode,
    } as CreatePostingBody;
    if (rate.trim()) {
      const pounds = Number(rate);
      if (Number.isNaN(pounds) || pounds < 0) {
        setError('Rate must be a number of pounds (e.g. 45 or 45.50).');
        return Promise.resolve();
      }
      (body as { rateAmount?: number }).rateAmount = Math.round(pounds * 100);
      (body as { rateVisible?: boolean }).rateVisible = rateVisible;
    }
    if (notes.trim()) (body as { notes?: string }).notes = notes.trim();

    return post
      .mutateAsync(body)
      .then(() => close())
      .catch((e: unknown) => {
        setError((e as Error)?.message ?? 'Could not post posting.');
      });
  }

  return (
    <Dialog open={open} onClose={close} title="Post a load">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Load" htmlFor="jTarget">
          <select
            id="jTarget"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm text-breaker-ink focus:outline-none focus:ring-2 focus:ring-breaker-identity/30"
          >
            <option value="">Choose...</option>
            {targetOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Assignment" htmlFor="jMode">
          <select
            id="jMode"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'claimable' | 'assign')}
            className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm text-breaker-ink focus:outline-none focus:ring-2 focus:ring-breaker-identity/30"
          >
            <option value="claimable">Claimable (drivers self-claim)</option>
            <option value="assign">Direct assign</option>
          </select>
        </Field>
        <Field label="Rate in GBP (optional)" htmlFor="jRate">
          <Input
            id="jRate"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="e.g. 45.00"
            inputMode="decimal"
          />
        </Field>
      </div>

      {rate.trim() ? (
        <label className="mt-3 flex items-center gap-2 text-sm text-breaker-ink-2">
          <input
            type="checkbox"
            checked={rateVisible}
            onChange={(e) => setRateVisible(e.target.checked)}
          />
          Show this rate to drivers
        </label>
      ) : null}

      <div className="mt-4">
        <Field label="Notes (optional)" htmlFor="jNotes">
          <Textarea
            id="jNotes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. AM slot, ring on arrival"
          />
        </Field>
      </div>

      {error ? <p className="text-xs text-breaker-red mt-3">{error}</p> : null}
      <div className="mt-5 flex justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={close}
          disabled={post.isPending}
        >
          Cancel
        </Button>
        <Button size="sm" type="button" action={onSubmit} loading={post.isPending}>
          Post load
        </Button>
      </div>
    </Dialog>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'px-4 py-2.5 text-2xs font-medium uppercase tracking-wider text-breaker-ink-2',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 align-middle', className)}>{children}</td>;
}
